/**
 * useStepConfig Hook - Access runtime step configuration from HTML injection
 *
 * This hook provides access to step-specific configuration that is injected
 * into the HTML by the CRM when the page is served. This includes:
 * - Payment flow overrides
 * - Static resources (offer, product IDs for A/B variants)
 * - Custom scripts
 * - Pixel tracking configuration
 *
 * Usage:
 * ```tsx
 * const { stepConfig, paymentFlowId, resources } = useStepConfig();
 *
 * // Access payment flow override
 * if (paymentFlowId) {
 *   console.log('Using custom payment flow:', paymentFlowId);
 * }
 *
 * // Access resource bindings (e.g., offer ID for current A/B variant)
 * const offerId = resources?.offer;
 * ```
 */
import { useMemo } from 'react';
import { getAssignedCheckoutOfferDefault, getAssignedCheckoutOfferIds, getAssignedOrderBumpOfferIds, getAssignedPaymentFlowId, getAssignedPixels, getAssignedResources, getAssignedScripts, getAssignedStepConfig, getAssignedUpsellOfferIds, } from '../../core/funnelClient';
/**
 * Hook to access runtime step configuration injected via HTML
 *
 * The step config is read from window.__TGD_STEP_CONFIG__ or the x-step-config meta tag.
 * Values are memoized and only re-computed on mount.
 *
 * @returns Step configuration values and helpers
 */
export function useStepConfig() {
    // Read once on mount - these values don't change during the page lifecycle
    const stepConfig = useMemo(() => getAssignedStepConfig(), []);
    const paymentFlowId = useMemo(() => getAssignedPaymentFlowId(), []);
    const resources = useMemo(() => getAssignedResources(), []);
    const pixels = useMemo(() => getAssignedPixels(), []);
    // Create a stable reference for getScripts
    const getScripts = useMemo(() => {
        return (position) => {
            return getAssignedScripts(position);
        };
    }, []);
    const paymentSetupConfig = useMemo(() => stepConfig?.paymentSetupConfig, [stepConfig]);
    const orderBumpOfferIds = useMemo(() => getAssignedOrderBumpOfferIds(), []);
    const upsellOfferIds = useMemo(() => getAssignedUpsellOfferIds(), []);
    const checkoutOfferIds = useMemo(() => getAssignedCheckoutOfferIds(), []);
    const checkoutOfferDefaultId = useMemo(() => getAssignedCheckoutOfferDefault(), []);
    return {
        stepConfig,
        paymentFlowId,
        resources,
        staticResources: resources,
        getScripts,
        pixels,
        paymentSetupConfig,
        orderBumpOfferIds,
        upsellOfferIds,
        checkoutOfferIds,
        checkoutOfferDefaultId,
    };
}
